'use client';

import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Wind,
  Snowflake,
  Droplets,
  Eye,
  CloudRain,
  Cloud,
  Gauge,
  ArrowUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { cloudCoverDescriptions } from '@/data/weather';
import {
  resolveWeatherLabels,
  type WeatherLabels,
  type ResolvedWeatherLabels,
} from '@/components/weather/labels';
import type { CurrentObservation } from '@/lib/fmi';

interface WeatherResponse {
  levi: CurrentObservation;
  yllas: CurrentObservation;
  fetchedAt: string;
}

function relativeTime(iso: string, L: ResolvedWeatherLabels): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 60) return L.updatedJustNow;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60)
    return minutes !== 1
      ? L.updatedMinutes.replace('{count}', String(minutes))
      : L.updatedMinute;
  const hours = Math.floor(minutes / 60);
  if (hours < 24)
    return hours !== 1
      ? L.updatedHours.replace('{count}', String(hours))
      : L.updatedHour;
  const days = Math.floor(hours / 24);
  return days !== 1
    ? L.updatedDays.replace('{count}', String(days))
    : L.updatedDay;
}

function feelsLike(temp: number | null, wind: number | null): number | null {
  if (temp === null) return null;
  if (temp >= 10 || wind === null || wind <= 0) return temp;
  return Math.round((temp - wind * 1.2) * 10) / 10;
}

function formatVisibility(meters: number | null): string {
  if (meters === null) return '--';
  if (meters > 1000) return `${(meters / 1000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

function cloudDesc(oktas: number | null, L: ResolvedWeatherLabels): string {
  if (oktas === null) return '--';
  return L.cloudDescriptions[oktas] ?? cloudCoverDescriptions[oktas] ?? '--';
}

function Row({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between rounded-lg px-3 py-2',
        'even:bg-[#F0EDE5]/50',
      )}
    >
      <div className="flex items-center gap-2">
        {icon}
        <span className={cn('text-sm', 'text-[#5A5A6E]')}>
          {label}
        </span>
      </div>
      <span className="text-sm font-medium">{value}</span>
    </div>
  );
}

function ResortColumn({
  data,
  name,
  L,
}: {
  data: CurrentObservation;
  name: string;
  L: ResolvedWeatherLabels;
}) {
  const feels = feelsLike(data.temperature, data.windSpeed);
  const iconClass = cn('h-4 w-4', 'text-[#5A5A6E]');

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5 }}
      className={cn(
        'flex-1 rounded-xl p-6 transition-colors duration-300',
        'bg-white shadow-sm',
      )}
    >
      <h2 className="text-lg font-light">{name}</h2>

      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-5xl font-sans font-semibold">
          {data.temperature !== null ? Math.round(data.temperature) : '--'}
        </span>
        <span className={cn('text-xl', 'text-[#5A5A6E]')}>
          °C
        </span>
      </div>

      {feels !== null && feels !== data.temperature && (
        <p className={cn('mt-1 text-sm', 'text-[#5A5A6E]')}>
          {L.feelsLike.replace('{temp}', String(Math.round(feels)))}
        </p>
      )}

      <div className="mt-5 space-y-0.5">
        <Row
          icon={<Wind className={iconClass} />}
          label={L.rowWind}
          value={
            <span className="flex items-center gap-1.5">
              {data.windSpeed !== null ? `${data.windSpeed} m/s` : '--'}
              {data.windDirection !== null && (
                <ArrowUp
                  className="h-3.5 w-3.5"
                  style={{ transform: `rotate(${data.windDirection}deg)` }}
                />
              )}
              {data.windGust !== null && (
                <span className={cn('text-xs', 'text-[#5A5A6E]')}>
                  ({data.windGust} m/s)
                </span>
              )}
            </span>
          }
        />
        <Row
          icon={<Droplets className={iconClass} />}
          label={L.rowHumidity}
          value={data.humidity !== null ? `${Math.round(data.humidity)}%` : '--'}
        />
        <Row
          icon={<Eye className={iconClass} />}
          label={L.rowVisibility}
          value={formatVisibility(data.visibility)}
        />
        <Row
          icon={<CloudRain className={iconClass} />}
          label={L.rowPrecipitation}
          value={data.precipitation1h !== null ? `${data.precipitation1h} mm` : '--'}
        />
        <Row
          icon={<Cloud className={iconClass} />}
          label={L.rowCloudCover}
          value={cloudDesc(data.cloudCover, L)}
        />
        <Row
          icon={<Snowflake className={iconClass} />}
          label={L.rowSnowDepth}
          value={data.snowDepth !== null ? `${Math.round(data.snowDepth)} cm` : '--'}
        />
        <Row
          icon={<Gauge className={iconClass} />}
          label={L.rowPressure}
          value={data.pressure !== null ? `${Math.round(data.pressure)} hPa` : '--'}
        />
      </div>

      <p className={cn('mt-5 text-xs', 'text-[#5A5A6E]/70')}>
        {L.source}
        <br />
        {relativeTime(data.timestamp, L)}
      </p>
    </motion.div>
  );
}

export default function WeatherDashboard({ labels }: { labels?: WeatherLabels }) {
  const L = resolveWeatherLabels(labels);
  const [data, setData] = useState<WeatherResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch('/api/weather')
      .then((res) => {
        if (!res.ok) throw new Error('fetch failed');
        return res.json();
      })
      .then((d) => setData(d))
      .catch(() => setError(true));
  }, []);

  if (error) {
    return (
      <div
        className={cn(
          'rounded-xl p-8 text-center text-sm',
          'bg-white text-[#5A5A6E] shadow-sm',
        )}
      >
        {L.unavailable}
      </div>
    );
  }

  if (!data) {
    return (
      <div className="grid gap-4 md:grid-cols-2">
        {[0, 1].map((i) => (
          <div
            key={i}
            className={cn(
              'animate-pulse rounded-xl p-6',
              'bg-white shadow-sm',
            )}
          >
            <div className={cn('h-5 w-20 rounded', 'bg-gray-200')} />
            <div className={cn('mt-4 h-14 w-28 rounded', 'bg-gray-200')} />
            <div className="mt-6 space-y-3">
              {Array.from({ length: 7 }).map((_, j) => (
                <div
                  key={j}
                  className={cn('h-8 rounded-lg', 'bg-gray-200')}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <ResortColumn data={data.levi} name="Levi" L={L} />
      <ResortColumn data={data.yllas} name="Ylläs" L={L} />
    </div>
  );
}
